import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";
import { api } from "./api";

/** Must match API_CACHE in src/sw.ts. */
const API_CACHE = "agent-api";
const DISMISS_KEY = "safiri-agent-install-dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type PushState = "unsupported" | "unavailable" | "denied" | "off" | "on";

interface Pwa {
  online: boolean;
  /** A new version is downloaded and waiting. */
  updateReady: boolean;
  applyUpdate: () => void;
  /** Running as an installed app. */
  standalone: boolean;
  /** Chrome/Edge/Android: we can show the native install dialog. */
  canInstall: boolean;
  /** iOS Safari: install is Share → Add to Home Screen, so we explain it. */
  iosInstall: boolean;
  install: () => Promise<void>;
  installDismissed: boolean;
  dismissInstall: () => void;
  push: PushState;
  pushBusy: boolean;
  enablePush: () => Promise<PushState>;
  disablePush: () => Promise<void>;
  refreshPush: () => Promise<void>;
  /** Bumped when a push arrives, so lists can reload. */
  pushTick: number;
}

const Ctx = createContext<Pwa | null>(null);

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function readDismissed() {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

/** Drop the offline copy of agent data: on sign-out and sign-in, so one browser never shows another agent's data. */
export async function clearApiCache() {
  try {
    if ("caches" in window) await caches.delete(API_CACHE);
  } catch {
    // Nothing cached.
  }
}

/** Remove this browser's push subscription (before the session that owns it ends). */
export async function unsubscribePush() {
  try {
    if (!pushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.post("/agent/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    // Best effort: the backend also drops subscriptions of ended sessions.
  }
}

export function PwaProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [updateReady, setUpdateReady] = useState(false);
  const updateRef = useRef<((reload?: boolean) => Promise<void>) | null>(null);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState(isStandalone);
  const [installDismissed, setInstallDismissed] = useState(readDismissed);
  const [push, setPush] = useState<PushState>(pushSupported() ? "off" : "unsupported");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushTick, setPushTick] = useState(0);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
    updateRef.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setUpdateReady(true),
      onRegisteredSW: (_url, reg) => {
        // Installed apps can stay open for days: look for a new version every hour.
        if (reg) setInterval(() => void reg.update().catch(() => undefined), 60 * 60 * 1000);
      },
    });
  }, []);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setStandalone(true);
    };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Messages from the service worker: a push arrived, or a tapped notification wants a page.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const on = (e: MessageEvent) => {
      if (e.data?.type === "PUSH_RECEIVED") setPushTick((n) => n + 1);
      if (e.data?.type === "NAVIGATE" && typeof e.data.path === "string") navigate(e.data.path);
    };
    navigator.serviceWorker.addEventListener("message", on);
    return () => navigator.serviceWorker.removeEventListener("message", on);
  }, [navigate]);

  const refreshPush = useCallback(async () => {
    if (!pushSupported()) return setPush("unsupported");
    try {
      const cfg = await api.get<{ enabled: boolean; publicKey: string | null }>("/agent/push/config");
      if (!cfg.enabled) return setPush("unavailable");
      if (Notification.permission === "denied") return setPush("denied");
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      // Re-attach to this session: a password change or new sign-in ends the old one.
      if (sub && Notification.permission === "granted") {
        const json = sub.toJSON();
        await api.post("/agent/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      }
      setPush(sub ? "on" : "off");
    } catch {
      // Signed out or offline: leave as is.
    }
  }, []);

  const enablePush = useCallback(async (): Promise<PushState> => {
    if (!pushSupported()) return "unsupported";
    setPushBusy(true);
    try {
      const cfg = await api.get<{ enabled: boolean; publicKey: string | null }>("/agent/push/config");
      if (!cfg.enabled || !cfg.publicKey) {
        setPush("unavailable");
        return "unavailable";
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        const next = permission === "denied" ? "denied" : "off";
        setPush(next);
        return next;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(cfg.publicKey) }));
      const json = sub.toJSON();
      await api.post("/agent/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setPush("on");
      return "on";
    } finally {
      setPushBusy(false);
    }
  }, []);

  const disablePush = useCallback(async () => {
    setPushBusy(true);
    try {
      await unsubscribePush();
      setPush(pushSupported() ? "off" : "unsupported");
    } finally {
      setPushBusy(false);
    }
  }, []);

  const value = useMemo<Pwa>(
    () => ({
      online,
      updateReady,
      applyUpdate: () => void updateRef.current?.(true),
      standalone,
      canInstall: Boolean(installEvent) && !standalone,
      iosInstall: isIos() && !standalone,
      install: async () => {
        if (!installEvent) return;
        await installEvent.prompt();
        await installEvent.userChoice.catch(() => undefined);
        setInstallEvent(null);
      },
      installDismissed,
      dismissInstall: () => {
        setInstallDismissed(true);
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now() + 14 * 86400_000));
        } catch {
          // Shown again next visit.
        }
      },
      push,
      pushBusy,
      enablePush,
      disablePush,
      refreshPush,
      pushTick,
    }),
    [online, updateReady, standalone, installEvent, installDismissed, push, pushBusy, enablePush, disablePush, refreshPush, pushTick]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePwa(): Pwa {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePwa outside PwaProvider");
  return v;
}
